import { type KeyboardEvent, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'
import { Alert } from '../feedback/Banner'

// Composer — reply to the client OR leave an internal note (design/components/
// inbox.html). The mode is announced and visually distinct (amber for notes) —
// critical so a note never leaks to the client. When the 24h WhatsApp window is
// closed, free replies are blocked with a clear template notice.

export interface ComposerProps {
  mode: 'reply' | 'note'
  onModeChange: (mode: 'reply' | 'note') => void
  onSend: (text: string, mode: 'reply' | 'note') => void
  windowOpen?: boolean
  /** Recipient first name for the reply placeholder. */
  recipientName?: string
  className?: string
}

/** Bandeja input with two modes: reply to the client, or internal note (amber
 *  and announced — a note must never leak to the client). Enter sends,
 *  Shift+Enter breaks line; with the WhatsApp 24h window closed, free replies
 *  are blocked behind a template notice. */
export function Composer({
  mode,
  onModeChange,
  onSend,
  windowOpen = true,
  recipientName,
  className,
}: ComposerProps) {
  const { t } = useTranslation('components')
  const [text, setText] = useState('')
  const inputId = useId()
  const isNote = mode === 'note'
  const replyBlocked = !windowOpen && mode === 'reply'

  function send() {
    const trimmed = text.trim()
    if (!trimmed || replyBlocked) return
    onSend(trimmed, mode)
    setText('')
  }
  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  const placeholder = isNote
    ? t('ui.composer.notePlaceholder')
    : recipientName
      ? t('ui.composer.replyPlaceholderNamed', { name: recipientName })
      : t('ui.composer.replyPlaceholder')

  return (
    <div
      className={cn('flex-none border-t border-line bg-surface px-[14px] pb-3 pt-[9px]', className)}
    >
      <div
        role="radiogroup"
        aria-label={t('ui.composer.modeLabel')}
        className="mb-[9px] flex gap-1"
      >
        <button
          type="button"
          role="radio"
          aria-checked={!isNote}
          onClick={() => onModeChange('reply')}
          className={cn(
            'rounded-md px-2.5 py-[5px] text-[11px] font-semibold transition-colors',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue',
            !isNote ? 'bg-field text-text' : 'text-text-2 hover:text-text',
          )}
        >
          {t('ui.composer.reply')}
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={isNote}
          onClick={() => onModeChange('note')}
          className={cn(
            'rounded-md px-2.5 py-[5px] text-[11px] font-semibold transition-colors',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue',
            isNote ? 'bg-warn-bg text-warn' : 'text-text-2 hover:text-text',
          )}
        >
          {t('ui.composer.note')}
        </button>
      </div>

      {replyBlocked && (
        <Alert tone="warn" className="mb-2">
          {t('ui.composer.windowClosed')}
        </Alert>
      )}

      <div
        className={cn(
          'rounded-xl border bg-bg',
          isNote ? 'border-[rgba(255,104,0,0.4)] bg-warn-bg/40' : 'border-line',
        )}
      >
        <label className="sr-only" htmlFor={inputId}>
          {isNote ? t('ui.composer.note') : t('ui.composer.replyFieldLabel')}
        </label>
        <textarea
          id={inputId}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={replyBlocked}
          placeholder={placeholder}
          className="min-h-[38px] w-full resize-none border-0 bg-transparent px-3 pb-1 pt-2.5 text-[12.5px] leading-normal text-text outline-none placeholder:text-text-3 disabled:cursor-not-allowed"
        />
        <div className="flex items-center gap-1 px-[7px] pb-[7px] pt-[5px]">
          <button
            type="button"
            aria-label={t('ui.composer.attach')}
            className="flex size-7 items-center justify-center rounded-md text-text-3 transition-colors hover:bg-hover hover:text-text-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue [&_svg]:size-4"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M21.4 11 12.3 20.2a5 5 0 0 1-7.1-7.1l8.5-8.5a3.33 3.33 0 0 1 4.7 4.7l-8.5 8.5a1.67 1.67 0 0 1-2.4-2.4l7.8-7.8" />
            </svg>
          </button>
          <span className="ml-auto mr-1 text-[10px] text-text-3">{t('ui.composer.enterHint')}</span>
          <button
            type="button"
            onClick={send}
            disabled={replyBlocked || text.trim().length === 0}
            className={cn(
              // min-h-10 keeps the primary send action a ≥40px touch target on
              // mobile (WCAG 2.5.5; accessibility-and-responsive §8).
              'flex min-h-10 items-center gap-1.5 rounded-lg px-3 py-[7px] text-[11.5px] font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:size-[14px]',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
              isNote
                ? 'bg-warn hover:bg-[#a05208] focus-visible:outline-warn'
                : 'bg-brand-blue hover:bg-[#1d4fd1] focus-visible:outline-brand-blue',
            )}
          >
            {isNote ? t('ui.composer.saveNote') : t('ui.composer.send')}
            {!isNote && (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M22 2 11 13M22 2l-7 20-4-9-9-4Z" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
